import { SURF_TIMEZONE, localDateKey } from './surf-forecast.mjs'

export const TIDE_REFERENCE = Object.freeze({ latitude: -8.8690049, longitude: 116.1415999 })
const HOUR = 3600
const finite = value => typeof value === 'number' && Number.isFinite(value)
const EPSILON = 0.000001

export function buildTideUrl() {
  // One shared regional reference, with boundary samples for the seven-day UI.
  return `https://marine-api.open-meteo.com/v1/marine?${new URLSearchParams({
    latitude: TIDE_REFERENCE.latitude, longitude: TIDE_REFERENCE.longitude,
    hourly: 'sea_level_height_msl', forecast_days: '8', past_days: '1',
    timezone: SURF_TIMEZONE, timeformat: 'unixtime', cell_selection: 'sea',
  })}`
}

export function sampledTideEvents(hours) {
  const events = []
  for (let i = 1; i < hours.length - 1; i++) {
    const before = hours[i - 1], row = hours[i]
    if (!finite(before.level) || !finite(row.level) || row.time - before.time !== HOUR) continue
    let end = i
    while (end + 1 < hours.length && hours[end + 1].time - hours[end].time === HOUR && finite(hours[end + 1].level) && Math.abs(hours[end + 1].level - row.level) < EPSILON) end++
    const after = hours[end + 1]
    if (after && finite(after.level) && after.time - hours[end].time === HOUR) {
      const high = row.level - before.level > EPSILON && row.level - after.level > EPSILON
      const low = before.level - row.level > EPSILON && after.level - row.level > EPSILON
      if (high || low) events.push({ type: high ? 'high' : 'low', time: row.time, endTime: hours[end].time, level: row.level })
    }
    i = end
  }
  return events
}

export function normalizeTideForecast(feed, retrievedAt) {
  if (feed?.timezone !== SURF_TIMEZONE || feed.utc_offset_seconds !== 28800 || feed.hourly_units?.time !== 'unixtime') throw new Error('Unexpected tide time basis')
  if (feed.hourly_units?.sea_level_height_msl !== 'm') throw new Error('Unexpected tide unit')
  if (!finite(feed.latitude) || !finite(feed.longitude) || Math.abs(feed.latitude - TIDE_REFERENCE.latitude) > .3 || Math.abs(feed.longitude - TIDE_REFERENCE.longitude) > .3) throw new Error('Unexpected tide grid')
  if (!Array.isArray(feed.hourly?.time) || !feed.hourly.time.length || feed.hourly.time.length > 240) throw new Error('Missing tide hours')
  const seen = new Set()
  const hours = feed.hourly.time.map((time, index) => {
    if (!Number.isInteger(time) || time <= 0 || time % HOUR !== 0 || seen.has(time)) throw new Error('Invalid tide timestamp')
    seen.add(time)
    const value = feed.hourly.sea_level_height_msl?.[index]
    return { time, level: finite(value) ? value : null }
  }).sort((a, b) => a.time - b.time)
  const valid = hours.filter(row => finite(row.level))
  if (!valid.length) throw new Error('No sea-level readings')
  return { hours, events: sampledTideEvents(hours), grid: { latitude: feed.latitude, longitude: feed.longitude },
    retrievedAt: new Date(retrievedAt).toISOString(), firstTime: valid[0].time, lastTime: valid.at(-1).time,
    provider: 'Météo-France SMOC via Open-Meteo', datum: 'Mean sea level', stale: false }
}

export function tideDayReading(tide, date, selectedTime) {
  const start = Date.parse(`${date}T00:00:00+08:00`) / 1000
  const byTime = new Map((tide?.hours || []).map(row => [row.time, row]))
  const rows = Array.from({ length: 24 }, (_, index) => ({ time: start + index * HOUR, level: byTime.get(start + index * HOUR)?.level ?? null }))
  const events = (tide?.events || []).filter(event => event.time < start + 86400 && event.endTime >= start)
  const reading = byTime.get(selectedTime)
  const level = reading && localDateKey(reading.time) === date ? reading.level : null
  let trend = null
  if (finite(level)) {
    const turning = (tide?.events || []).find(event => event.time <= selectedTime && event.endTime >= selectedTime)
    const next = byTime.get(selectedTime + HOUR)?.level
    const previous = byTime.get(selectedTime - HOUR)?.level
    const change = finite(next) ? next - level : finite(previous) ? level - previous : null
    trend = turning ? `Near estimated ${turning.type} water` : change === null ? null : Math.abs(change) < EPSILON ? 'Little change' : change > 0 ? 'Rising' : 'Falling'
  }
  // Never search forward across a missing interval and call it the "next" tide.
  const nextCandidate = (tide?.events || []).find(event => event.time > selectedTime)
  let nextEvent = null
  if (finite(level) && nextCandidate) {
    let complete = true
    for (let time = selectedTime; time <= nextCandidate.endTime; time += HOUR) if (!finite(byTime.get(time)?.level)) { complete = false; break }
    if (complete) nextEvent = nextCandidate
  }
  return { rows, events, level: finite(level) ? level : null, trend, nextEvent, validCount: rows.filter(row => finite(row.level)).length }
}

// The same dimensions drive hour buttons and tide points in their shared rail.
export const TIMELINE_HOUR_WIDTH = 62
export const TIMELINE_HOUR_GAP = 3
export const TIMELINE_HOUR_PADDING = 4
export function alignedTidePoints(hours, tide) {
  const levels = new Map((tide?.hours || []).map(row => [row.time, row.level]))
  return {
    width: hours.length ? hours.length * TIMELINE_HOUR_WIDTH + (hours.length - 1) * TIMELINE_HOUR_GAP + 2 * TIMELINE_HOUR_PADDING : 0,
    points: hours.map((row, index) => ({
      time: row.time, x: TIMELINE_HOUR_PADDING + TIMELINE_HOUR_WIDTH / 2 + index * (TIMELINE_HOUR_WIDTH + TIMELINE_HOUR_GAP),
      level: finite(levels.get(row.time)) ? levels.get(row.time) : null,
    })),
  }
}
